import {
  ArrowPathIcon,
  ArrowTrendingUpIcon,
  BoltIcon,
  CalendarDaysIcon,
  CheckBadgeIcon,
  CheckCircleIcon,
  ClockIcon,
  EnvelopeIcon,
  ExclamationCircleIcon,
  FunnelIcon,
  StarIcon,
  UserCircleIcon,
  UserGroupIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api } from '../lib/api';
import { downloadCsv } from '../lib/csvExport';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';

/* ── helpers ─────────────────────────────────────────────────────────────── */
const fmtMin = (m) => {
  if (!m || m === 0) return '—';
  const h = Math.floor(m / 60);
  const min = m % 60;
  return h > 0 ? `${h}h ${min}m` : `${min}m`;
};

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Never';

const PIE_COLORS = ['#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#06b6d4', '#f43f5e', '#84cc16'];

const RANGE_OPTIONS = [
  { label: 'Last 7 days',   value: 7 },
  { label: 'Last 30 days',  value: 30 },
  { label: 'Last 90 days',  value: 90 },
];

const PERF_COLOR = (score) =>
  score >= 75 ? '#10b981' : score >= 50 ? '#f59e0b' : '#ef4444';

const scoreLabel = (score) =>
  score >= 75 ? 'High' : score >= 50 ? 'Medium' : 'Low';

/* ── KPI card ─────────────────────────────────────────────────────────────── */
function KPICard({ title, value, sub, icon: Icon, color, trend }) {
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
      <Card className="relative overflow-hidden border-0 shadow-md">
        <div className="absolute inset-0 opacity-[0.06]" style={{ background: color }} />
        <CardContent className="p-5 flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl shadow-sm" style={{ background: color }}>
            <Icon className="h-6 w-6 text-white" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{title}</p>
            <p className="mt-0.5 text-2xl font-bold text-gray-900">{value}</p>
            {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
            {trend != null && (
              <p className={`text-xs font-medium mt-1 ${trend >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                {trend >= 0 ? '▲' : '▼'} {Math.abs(trend)}%
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

/* ── Staff detail popover ─────────────────────────────────────────────────── */
function StaffDetailPanel({ staff, onClose }) {
  if (!staff) return null;
  return (
    <motion.div
      initial={{ opacity: 0, x: 40 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 40 }}
      className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-sm bg-white shadow-2xl border-l border-gray-100 overflow-y-auto"
    >
      <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between z-10">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full text-white font-bold text-lg"
            style={{ background: `linear-gradient(135deg,${PERF_COLOR(staff.perfScore)},#6366f1)` }}>
            {staff.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="font-semibold text-gray-900">{staff.name}</p>
            <p className="text-xs text-gray-500 capitalize">{staff.role}</p>
          </div>
        </div>
        <button onClick={onClose} className="rounded-lg p-1.5 hover:bg-gray-100 text-gray-400 transition-colors">
          <span className="text-lg font-bold leading-none">×</span>
        </button>
      </div>

      <div className="p-5 space-y-5">
        {/* Performance badge */}
        <div className="flex items-center justify-between rounded-xl p-4 border" style={{ borderColor: PERF_COLOR(staff.perfScore) + '40', background: PERF_COLOR(staff.perfScore) + '0a' }}>
          <span className="text-sm font-medium text-gray-700">Performance Score</span>
          <span className="text-2xl font-bold" style={{ color: PERF_COLOR(staff.perfScore) }}>{staff.perfScore}/100</span>
        </div>

        {/* Session stats */}
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Sessions & Time</p>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Total Logins', value: staff.loginCount },
              { label: 'Total Time', value: fmtMin(staff.totalTimeMin) },
              { label: 'Avg Session', value: fmtMin(staff.avgSessionMin) },
              { label: 'Last Login', value: fmtDate(staff.lastLogin) },
            ].map(({ label, value }) => (
              <div key={label} className="rounded-lg bg-gray-50 p-3">
                <p className="text-xs text-gray-500">{label}</p>
                <p className="text-sm font-semibold text-gray-800 mt-0.5">{value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Task stats */}
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Task Performance</p>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Created', value: staff.tasksCreated, color: '#6366f1' },
              { label: 'Assigned', value: staff.tasksAssigned, color: '#f59e0b' },
              { label: 'Completed', value: staff.tasksCompleted, color: '#10b981' },
              { label: 'Pending', value: staff.tasksPending, color: '#06b6d4' },
              { label: 'Overdue', value: staff.tasksOverdue, color: '#ef4444' },
              { label: 'Completion %', value: `${staff.completionRate}%`, color: PERF_COLOR(staff.completionRate) },
            ].map(({ label, value, color }) => (
              <div key={label} className="rounded-lg bg-gray-50 p-3">
                <p className="text-xs text-gray-500">{label}</p>
                <p className="text-sm font-bold mt-0.5" style={{ color }}>{value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Lead stats */}
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Lead Activity</p>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Total Leads', value: staff.leadsTotal, color: '#6366f1' },
              { label: 'Converted', value: staff.leadsConverted, color: '#10b981' },
              { label: 'Lost', value: staff.leadsLost, color: '#ef4444' },
              { label: 'Conv. Rate', value: `${staff.conversionRate}%`, color: PERF_COLOR(staff.conversionRate) },
            ].map(({ label, value, color }) => (
              <div key={label} className="rounded-lg bg-gray-50 p-3">
                <p className="text-xs text-gray-500">{label}</p>
                <p className="text-sm font-bold mt-0.5" style={{ color }}>{value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Misc */}
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Other Activity</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg bg-gray-50 p-3">
              <p className="text-xs text-gray-500">Emails Sent</p>
              <p className="text-sm font-semibold text-gray-800 mt-0.5">{staff.emailsSent}</p>
            </div>
            <div className="rounded-lg bg-gray-50 p-3">
              <p className="text-xs text-gray-500">Audit Actions</p>
              <p className="text-sm font-semibold text-gray-800 mt-0.5">{staff.auditActions}</p>
            </div>
          </div>
          {staff.topModules?.length > 0 && (
            <div className="mt-3">
              <p className="text-xs text-gray-500 mb-1.5">Top Modules Used</p>
              <div className="flex flex-wrap gap-1.5">
                {staff.topModules.map((m) => (
                  <span key={m} className="inline-flex items-center rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-700">{m}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

/* ── Main component ───────────────────────────────────────────────────────── */
export default function StaffAnalytics() {
  const [data,          setData]          = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [range,         setRange]         = useState(30);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [search,        setSearch]        = useState('');
  const [roleFilter,    setRoleFilter]    = useState('all');
  const panelRef = useRef(null);

  const load = async (r = range) => {
    setLoading(true);
    try {
      const res = await api.get(`/analytics/staff?range=${r}`);
      if (res.data.success) setData(res.data.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(range); }, [range]);

  // Close panel on outside click
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setSelectedStaff(null);
    };
    if (selectedStaff) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [selectedStaff]);

  const handleExportStaffAnalytics = () => {
    if (!filteredRows.length) return;
    const headers = [
      'Name', 'Email', 'Role', 'Status',
      'Total Logins', 'Total Time (min)', 'Avg Session (min)',
      'Tasks Created', 'Tasks Assigned', 'Tasks Completed', 'Tasks Pending', 'Tasks Overdue', 'Completion %',
      'Leads Total', 'Leads Converted', 'Leads Lost', 'Conv Rate %',
      'Emails Sent', 'Audit Actions', 'Performance Score',
    ];
    const rows = filteredRows.map((s) => [
      s.name,        s.email,        s.role,
      s.isActive ? 'Active' : 'Inactive',
      s.loginCount,  s.totalTimeMin, s.avgSessionMin,
      s.tasksCreated,s.tasksAssigned,s.tasksCompleted,s.tasksPending,s.tasksOverdue, s.completionRate,
      s.leadsTotal,  s.leadsConverted,s.leadsLost, s.conversionRate,
      s.emailsSent,  s.auditActions, s.perfScore,
    ]);
    downloadCsv(headers, rows, `staff-analytics-${range}days-${new Date().toISOString().slice(0,10)}.csv`);
    toast.success(`Exported ${rows.length} staff records`);
  };

  const filteredRows = (data?.staffRows || []).filter(s => {
    const matchSearch = !search || s.name.toLowerCase().includes(search.toLowerCase()) || s.email.toLowerCase().includes(search.toLowerCase());
    const matchRole   = roleFilter === 'all' || s.role === roleFilter;
    return matchSearch && matchRole;
  });

  const { summary } = data || {};

  return (
    <div className="space-y-6">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Staff Analytics</h1>
          <p className="text-sm text-gray-500 mt-0.5">Login sessions, tasks, leads and email activity per staff member</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Range selector */}
          <div className="flex rounded-lg border border-gray-200 overflow-hidden bg-white shadow-sm">
            {RANGE_OPTIONS.map(({ label, value }) => (
              <button
                key={value}
                onClick={() => setRange(value)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${range === value ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
              >
                {label}
              </button>
            ))}
          </div>
          <button
            onClick={() => load(range)}
            disabled={loading}
            className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 shadow-sm transition-colors"
          >
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {loading && !data ? (
        <div className="flex items-center justify-center py-24">
          <div className="flex flex-col items-center gap-3">
            <ArrowPathIcon className="h-10 w-10 text-indigo-400 animate-spin" />
            <p className="text-sm text-gray-500">Loading analytics…</p>
          </div>
        </div>
      ) : !data ? (
        <div className="flex items-center justify-center py-24 text-gray-400">No data available</div>
      ) : (
        <>
          {/* ── KPI Row ──────────────────────────────────────────────────────── */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            <KPICard title="Total Logins"     value={summary.totalLogins}            sub={`Last ${range} days`}  icon={BoltIcon}            color="#6366f1" />
            <KPICard title="Active Staff"     value={summary.activeStaff}            sub={`of ${summary.totalStaff} staff`} icon={UsersIcon} color="#10b981" />
            <KPICard title="Tasks Completed"  value={summary.totalTasksCompleted}    sub={`${summary.overallCompletionRate}% rate`} icon={CheckCircleIcon} color="#f59e0b" />
            <KPICard title="Leads Converted"  value={summary.totalConverted}         sub={`of ${summary.totalLeads} leads`} icon={ArrowTrendingUpIcon} color="#06b6d4" />
            <KPICard title="Emails Sent"      value={summary.totalEmailsSent}        sub={`${summary.totalEmailsFailed} failed`} icon={EnvelopeIcon} color="#8b5cf6" />
          </div>

          {/* ── Charts Row ──────────────────────────────────────────────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Daily Login Trend */}
            <Card className="lg:col-span-2 shadow-sm border-0">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <BoltIcon className="h-4 w-4 text-indigo-500" />
                  Daily Login Trend
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={data.dailyLoginTrend} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={Math.floor(data.dailyLoginTrend.length / 8)} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                    <Tooltip contentStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="logins" stroke="#6366f1" strokeWidth={2} dot={false} name="Logins" />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Module Usage Pie */}
            <Card className="shadow-sm border-0">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <FunnelIcon className="h-4 w-4 text-amber-500" />
                  Module Usage
                </CardTitle>
              </CardHeader>
              <CardContent>
                {data.moduleUsageBreakdown.length === 0 ? (
                  <div className="flex items-center justify-center h-[220px] text-gray-400 text-sm">No audit data yet</div>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={data.moduleUsageBreakdown} dataKey="count" nameKey="module" cx="50%" cy="50%" outerRadius={80} label={({ module, percent }) => `${module} ${Math.round(percent * 100)}%`} labelLine={false} fontSize={10}>
                        {data.moduleUsageBreakdown.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>

          {/* ── Charts Row 2 ─────────────────────────────────────────────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Task type breakdown */}
            <Card className="shadow-sm border-0">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <CheckBadgeIcon className="h-4 w-4 text-emerald-500" />
                  Tasks by Type
                </CardTitle>
              </CardHeader>
              <CardContent>
                {data.taskTypeBreakdown.length === 0 ? (
                  <div className="flex items-center justify-center h-[180px] text-gray-400 text-sm">No task data</div>
                ) : (
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={data.taskTypeBreakdown} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="type" tick={{ fontSize: 10 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                      <Tooltip contentStyle={{ fontSize: 12 }} />
                      <Bar dataKey="count" name="Tasks" radius={[4, 4, 0, 0]}>
                        {data.taskTypeBreakdown.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Email type breakdown */}
            <Card className="shadow-sm border-0">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <EnvelopeIcon className="h-4 w-4 text-purple-500" />
                  Emails by Type
                </CardTitle>
              </CardHeader>
              <CardContent>
                {data.emailTypeBreakdown.length === 0 ? (
                  <div className="flex items-center justify-center h-[180px] text-gray-400 text-sm">No email data</div>
                ) : (
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={data.emailTypeBreakdown} layout="vertical" margin={{ top: 4, right: 8, left: 40, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} />
                      <YAxis dataKey="type" type="category" tick={{ fontSize: 10 }} width={80} />
                      <Tooltip contentStyle={{ fontSize: 12 }} />
                      <Bar dataKey="count" name="Emails" radius={[0, 4, 4, 0]}>
                        {data.emailTypeBreakdown.map((_, i) => <Cell key={i} fill={PIE_COLORS[(i + 3) % PIE_COLORS.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>

          {/* ── Staff Table ──────────────────────────────────────────────────── */}
          <Card className="shadow-sm border-0">
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <UserGroupIcon className="h-4 w-4 text-indigo-500" />
                  Staff Performance ({filteredRows.length})
                </CardTitle>
                <div className="flex items-center gap-2 flex-wrap">
                  <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search staff…"
                    className="h-8 w-40 rounded-lg border border-gray-200 px-3 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-300"
                  />
                  <select
                    value={roleFilter}
                    onChange={e => setRoleFilter(e.target.value)}
                    className="h-8 rounded-lg border border-gray-200 px-2 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-300"
                  >
                    <option value="all">All Roles</option>
                    <option value="admin">Admin</option>
                    <option value="counselor">Counselor</option>
                  </select>
                  {filteredRows.length > 0 && (
                    <button
                      onClick={handleExportStaffAnalytics}
                      className="flex items-center gap-1.5 rounded-lg border border-indigo-300 bg-white px-3 h-8 text-xs font-medium text-indigo-700 hover:bg-indigo-50 transition-colors"
                    >
                      ↓ Export CSV
                    </button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gradient-to-r from-indigo-600 to-violet-600">
                      {['Staff Member', 'Role', 'Logins', 'Time Spent', 'Tasks Done', 'Completion %', 'Leads', 'Conv %', 'Emails', 'Score'].map(h => (
                        <TableHead key={h} className="text-white text-xs font-semibold py-3 whitespace-nowrap">{h}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center py-8 text-gray-400 text-sm">No staff found</TableCell>
                      </TableRow>
                    ) : filteredRows.map((s, idx) => (
                      <TableRow
                        key={s.userId}
                        className={`cursor-pointer transition-colors hover:bg-indigo-50 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                        onClick={() => setSelectedStaff(s)}
                      >
                        <TableCell className="py-3">
                          <div className="flex items-center gap-2.5">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white text-sm font-bold"
                              style={{ background: `linear-gradient(135deg,${PERF_COLOR(s.perfScore)},#6366f1)` }}>
                              {s.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{s.name}</p>
                              <p className="text-xs text-gray-400">{s.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`text-xs capitalize ${s.role === 'admin' ? 'border-indigo-300 text-indigo-700' : 'border-emerald-300 text-emerald-700'}`}>
                            {s.role}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm font-medium">{s.loginCount}</TableCell>
                        <TableCell className="text-sm">{fmtMin(s.totalTimeMin)}</TableCell>
                        <TableCell>
                          <span className="text-sm font-medium text-emerald-700">{s.tasksCompleted}</span>
                          <span className="text-xs text-gray-400"> / {s.tasksAssigned}</span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="h-1.5 w-16 rounded-full bg-gray-200 overflow-hidden">
                              <div className="h-full rounded-full" style={{ width: `${s.completionRate}%`, background: PERF_COLOR(s.completionRate) }} />
                            </div>
                            <span className="text-xs font-medium" style={{ color: PERF_COLOR(s.completionRate) }}>{s.completionRate}%</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{s.leadsTotal}</TableCell>
                        <TableCell>
                          <span className="text-xs font-medium" style={{ color: PERF_COLOR(s.conversionRate) }}>{s.conversionRate}%</span>
                        </TableCell>
                        <TableCell className="text-sm">{s.emailsSent}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <div className="flex h-7 w-7 items-center justify-center rounded-full text-white text-xs font-bold"
                              style={{ background: PERF_COLOR(s.perfScore) }}>
                              {s.perfScore}
                            </div>
                            <span className="text-xs" style={{ color: PERF_COLOR(s.perfScore) }}>{scoreLabel(s.perfScore)}</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Legend */}
              <div className="px-4 py-3 border-t border-gray-100 flex flex-wrap items-center gap-4 text-xs text-gray-500">
                <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500 inline-block" /> High performer ≥75</span>
                <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-amber-400 inline-block" /> Medium 50-74</span>
                <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-red-400 inline-block" /> Needs attention &lt;50</span>
                <span className="ml-auto italic">Click any row for full details</span>
              </div>
            </CardContent>
          </Card>

          {/* ── Top Performers Podium ─────────────────────────────────────────── */}
          {filteredRows.length >= 3 && (
            <Card className="shadow-sm border-0">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <StarIcon className="h-4 w-4 text-amber-500" />
                  Top Performers
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {filteredRows.slice(0, 3).map((s, i) => {
                    const medals = ['🥇', '🥈', '🥉'];
                    return (
                      <motion.div
                        key={s.userId}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.1 }}
                        className="cursor-pointer rounded-xl border p-4 text-center hover:shadow-md transition-shadow"
                        style={{ borderColor: PERF_COLOR(s.perfScore) + '50', background: PERF_COLOR(s.perfScore) + '08' }}
                        onClick={() => setSelectedStaff(s)}
                      >
                        <p className="text-3xl mb-2">{medals[i]}</p>
                        <div className="flex h-12 w-12 items-center justify-center rounded-full text-white text-lg font-bold mx-auto mb-2"
                          style={{ background: `linear-gradient(135deg,${PERF_COLOR(s.perfScore)},#6366f1)` }}>
                          {s.name.charAt(0).toUpperCase()}
                        </div>
                        <p className="font-semibold text-gray-900 text-sm">{s.name}</p>
                        <p className="text-xs text-gray-500 capitalize mb-2">{s.role}</p>
                        <div className="flex items-center justify-center gap-1">
                          <span className="text-xl font-bold" style={{ color: PERF_COLOR(s.perfScore) }}>{s.perfScore}</span>
                          <span className="text-xs text-gray-400">/100</span>
                        </div>
                        <div className="mt-2 grid grid-cols-3 gap-1 text-center">
                          <div><p className="text-xs text-gray-400">Logins</p><p className="text-xs font-bold text-gray-700">{s.loginCount}</p></div>
                          <div><p className="text-xs text-gray-400">Tasks</p><p className="text-xs font-bold text-gray-700">{s.tasksCompleted}</p></div>
                          <div><p className="text-xs text-gray-400">Leads</p><p className="text-xs font-bold text-gray-700">{s.leadsConverted}</p></div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* ── Alerts ───────────────────────────────────────────────────────── */}
          {filteredRows.some(s => s.tasksOverdue > 0) && (
            <Card className="shadow-sm border border-red-100 bg-red-50/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-red-700 flex items-center gap-2">
                  <ExclamationCircleIcon className="h-4 w-4" />
                  Overdue Task Alerts
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  {filteredRows.filter(s => s.tasksOverdue > 0).map(s => (
                    <button
                      key={s.userId}
                      onClick={() => setSelectedStaff(s)}
                      className="flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-2 text-sm hover:bg-red-50 transition-colors"
                    >
                      <span className="font-medium text-gray-800">{s.name}</span>
                      <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-bold text-red-700">{s.tasksOverdue} overdue</span>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* ── Staff Detail Panel ───────────────────────────────────────────────── */}
      <div ref={panelRef}>
        {selectedStaff && (
          <StaffDetailPanel
            staff={selectedStaff}
            onClose={() => setSelectedStaff(null)}
          />
        )}
      </div>
    </div>
  );
}
